import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, AlertTriangle, Heart } from 'lucide-react';

export default function MedicalDisclaimer() {
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                <span className="font-bold text-lg text-white">Autoimmune Pathways</span>
                                <span className="text-xs sm:text-sm text-slate-400">by Me and My Pandas</span>
                            </div>
                        </Link>
                        <Link to="/">
                            <Button variant="ghost" className="text-slate-300 hover:text-white gap-2">
                                <ArrowLeft className="w-4 h-4" />
                                Back to Home
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-6">
                            <AlertTriangle className="w-8 h-8 text-amber-400" />
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            Medical Disclaimer
                        </h1>
                        <p className="text-lg text-slate-400">
                            Important information about using Autoimmune Pathways
                        </p>
                    </div>

                    {/* Disclaimer Content */}
                    <Card className="bg-slate-900/50 border-slate-700">
                        <CardContent className="p-8 prose prose-invert max-w-none">
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-8">
                                <p className="text-amber-200 font-medium m-0">
                                    Please read this disclosure carefully before using Autoimmune Pathways. This information is essential for understanding the purpose and limitations of our application.
                                </p>
                            </div>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">Medical & Educational Disclaimer</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    The Autoimmune Pathways application ("the App") is an educational and organizational tool designed to support individuals in understanding and navigating complex autoimmune-related experiences, including but not limited to Lupus, Rheumatoid Arthritis, Multiple Sclerosis, Hashimoto's Thyroiditis, Crohn's Disease, Psoriasis, and other autoimmune conditions.
                                </p>
                                <p className="text-amber-300 font-semibold mt-4">
                                    This app does not provide medical advice, diagnosis, or treatment.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">Educational Use Only</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    All information provided within the App is for educational and informational purposes only. The App does not provide medical advice, diagnosis, treatment, or clinical decision-making, and it is not intended to replace consultation with a licensed physician, rheumatologist, immunologist, or other qualified healthcare provider.
                                </p>
                                <p className="text-slate-400 italic mt-4">
                                    Use of the App does not create a provider-patient relationship.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">No Medical Advice or Treatment</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    Content within the App — including educational materials, tracking tools, logs, charts, summaries, exports, supplement information, food guidance, environmental context, AI correlations, or messaging features — should not be interpreted as medical advice or a recommendation for diagnosis, treatment, medication, supplementation, or therapeutic intervention.
                                </p>
                                <p className="text-slate-300 leading-relaxed mt-4">
                                    Medical decisions should always be made in consultation with a licensed healthcare provider who is familiar with your individual medical history and circumstances.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">Supplement & Nutrition Information</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    Any information related to supplements, nutrition, food patterns, or lifestyle considerations is provided for general educational context only and reflects commonly discussed topics within scientific or clinical literature. Such information is not individualized and may not be appropriate for all individuals.
                                </p>
                                <p className="text-slate-300 leading-relaxed mt-4">
                                    Supplement dosing ranges, weight-based calculations, or food guidance shown in the App are not generalized recommendations and should not be interpreted as individual recommendations or prescriptions. Always review supplement use, nutrition changes, and dosing with your primary care provider or qualified healthcare professional before starting, stopping, or modifying any supplement, medication, or dietary approach.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">Tracking, Logs & Pattern Data</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    The App may allow users to log symptoms, flares, food intake, supplements, medications, lab results, environmental factors, or other observations. These features are intended to help organize information and support reflection over time.
                                </p>
                                <p className="text-slate-300 leading-relaxed mt-4">
                                    Users are encouraged to share logged information with their healthcare providers for discussion and clinical interpretation as appropriate.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">AI Correlations & Insights</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    The App includes AI-powered features that analyze your tracking data to identify potential patterns and correlations between various factors (food, sleep, weather, symptoms, etc.). These insights are generated algorithmically and are meant to support self-reflection and conversations with your healthcare team.
                                </p>
                                <p className="text-amber-300 font-medium mt-4">
                                    AI-generated insights are not medical diagnoses or treatment recommendations. Always verify patterns with your healthcare provider before making changes to your treatment plan.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">No Emergency or Crisis Support</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    The App is not designed for emergency use and should not be used in place of emergency services. If you are experiencing a medical emergency or are at risk of harm, contact local emergency services or a qualified healthcare professional immediately.
                                </p>
                                <p className="text-slate-400 italic mt-4">
                                    The App does not provide crisis intervention, emergency monitoring, or real-time medical support.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">Messaging & Support Features</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    If the App includes messaging or communication features, these are provided for educational support and general guidance only. Messaging does not constitute medical care, diagnosis, or treatment, and response times may vary.
                                </p>
                                <p className="text-slate-300 leading-relaxed mt-4">
                                    No messaging feature within the App should be used to seek urgent medical advice, emergency care, or crisis intervention.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">Community Content Disclaimer</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    If the App includes community or peer discussion features, content shared by users reflects individual experiences and opinions and does not represent medical advice or endorsed guidance.
                                </p>
                                <p className="text-slate-300 leading-relaxed mt-4">
                                    Autoimmune Pathways does not verify, validate, or endorse user-generated content and is not responsible for the accuracy of statements made by other users.
                                </p>
                            </section>

                            <section className="mb-8">
                                <h2 className="text-xl font-semibold text-white mb-4">External Links & Third-Party Content</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    The App may include links to external websites, resources, products, or services for informational convenience. Autoimmune Pathways does not control, endorse, or assume responsibility for the content, accuracy, or practices of third-party sites.
                                </p>
                                <p className="text-slate-400 italic mt-4">
                                    Accessing external links is done at the user's discretion.
                                </p>
                            </section>

                            <section className="mb-8 bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-6">
                                <h2 className="text-xl font-semibold text-white mb-4">Acknowledgment</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    By using Autoimmune Pathways, you acknowledge that you have read, understood, and agree to the terms outlined in this Medical Disclaimer. You understand that the App is an educational tool and does not replace professional medical care.
                                </p>
                                <p className="text-indigo-300 font-semibold mt-4">
                                    Always consult with qualified healthcare professionals for medical advice, diagnosis, and treatment.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">Questions?</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    If you have questions about this Medical Disclaimer, please contact us:
                                </p>
                                <p className="text-slate-300 mt-4">
                                    <strong>Email:</strong>{' '}
                                    <a href="mailto:contact@meandmypandas.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                                        contact@meandmypandas.com
                                    </a>
                                </p>
                            </section>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-slate-500">
                        © {new Date().getFullYear()} Autoimmune Pathways. All rights reserved.
                    </p>
                    <div className="flex items-center gap-6 text-sm">
                        <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">
                            Terms of Service
                        </Link>
                        <Link to="/faq" className="text-slate-400 hover:text-white transition-colors">
                            FAQs
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
